'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { getMobileStorePlatform, getMobileStoreUrl, type MobileStorePlatform } from '@/components/mobile-browser-store-banner';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatWebSunsetDate } from '@/lib/web-sunset-date';
import { isPierreWebViewUserAgent } from '@/lib/webview/is-pierre-webview';
const WEB_SUNSET_QR_CODE_SRC = '/images/web-sunset-qr-code.svg';
function isWebView() {
  return Boolean(window.ReactNativeWebView) || isPierreWebViewUserAgent(window.navigator.userAgent);
}
function CloseButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DialogClose asChild={true}><button type="button" aria-label="Fechar aviso" className="absolute right-6 top-6 z-10 flex size-10 items-center justify-center rounded-full border border-white/10 bg-zinc-700/70 text-white shadow-[0_8px_40px_rgba(0,0,0,0.12)] backdrop-blur-md transition-colors hover:bg-zinc-600/80"><X className="size-5" aria-hidden="true" /></button></DialogClose>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function HeroImage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative h-44 w-full shrink-0 overflow-hidden"><picture><source media="(max-width: 767px)" srcSet="/images/web-sunset-modal-mobile.webp" /><img src="/images/web-sunset-modal-desktop.webp" alt="" fetchPriority="high" decoding="async" className="absolute inset-0 size-full object-cover object-center" /></picture><CloseButton /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function DesktopContent(t0) {
  const $ = _c(5);
  const {
    sunsetDateLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeroImage />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex w-[432px] flex-col items-center gap-3 text-center"><DialogTitle className="text-3xl font-medium leading-none text-white">O melhor do Pierre está no app</DialogTitle><DialogDescription className="text-lg leading-6 text-zinc-400">No aplicativo, você encontra uma experiência mais completa, e novas formas de cuidar das suas transações.</DialogDescription></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex size-[180px] items-center justify-center rounded-[20px] bg-zinc-50"><Image src={WEB_SUNSET_QR_CODE_SRC} alt="QR Code para baixar o aplicativo Pierre" width={169} height={166} fetchPriority="high" priority={true} /></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== sunsetDateLabel) {
    t4 = <>{t1}{t2}{t3}<p className="text-center text-lg leading-6 text-zinc-400">A partir de {sunsetDateLabel}, a versão web será descontinuada.</p></>;
    $[3] = sunsetDateLabel;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function MobileContent(t0) {
  const $ = _c(16);
  const {
    platform,
    sunsetDateLabel
  } = t0;
  const isAndroid = platform === "android";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HeroImage />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col items-center gap-2"><DialogTitle className="w-[289px] text-2xl font-medium leading-8 text-white">O melhor do Pierre está no app</DialogTitle><DialogDescription className="text-base leading-6 text-zinc-400">No aplicativo, você encontra uma experiência mais completa, e novas formas de cuidar das suas transações.</DialogDescription></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== platform) {
    t3 = getMobileStoreUrl(platform);
    $[2] = platform;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isAndroid ? "Baixar na Google Play" : "Baixar na App Store";
  const t5 = isAndroid ? "/images/google-play-badge.webp" : "/images/web-sunset-app-store-badge.png";
  const t6 = isAndroid ? 135 : 120;
  let t7;
  if ($[4] !== t5 || $[5] !== t6) {
    t7 = <Image src={t5} alt="" width={t6} height={40} />;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t7) {
    t8 = <div className="flex w-[306px] flex-col items-center gap-6 text-center">{t2}<Link href={t3} target="_blank" rel="noreferrer" aria-label={t4}>{t7}</Link></div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== sunsetDateLabel) {
    t9 = <p className="w-[306px] text-center text-base leading-6 text-zinc-400">A partir de {sunsetDateLabel}, a versão web será descontinuada.</p>;
    $[11] = sunsetDateLabel;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <>{t1}{t8}{t9}</>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
export function WebSunsetPostLoginModal(t0) {
  const $ = _c(17);
  const {
    sunsetDate
  } = t0;
  const pathname = usePathname();
  const {
    status
  } = useSession();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [shownForSession, setShownForSession] = useState(false);
  const [platform, setPlatform] = useState("ios");
  let t1;
  if ($[0] !== sunsetDate) {
    t1 = formatWebSunsetDate(sunsetDate);
    $[0] = sunsetDate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sunsetDateLabel = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp, t2);
  let t3;
  let t4;
  if ($[3] !== pathname || $[4] !== shownForSession || $[5] !== status || $[6] !== sunsetDateLabel) {
    t3 = () => {
      if (status === "unauthenticated") {
        setShownForSession(false);
        return;
      }
      if (!sunsetDateLabel || shownForSession || status !== "authenticated" || pathname.startsWith("/login") || pathname.startsWith("/backoffice") || isWebView()) {
        return;
      }
      const detectedPlatform = getMobileStorePlatform({
        userAgent: window.navigator.userAgent,
        maxTouchPoints: window.navigator.maxTouchPoints,
        platform: window.navigator.platform
      });
      if (detectedPlatform) {
        setPlatform(detectedPlatform);
      }
      setOpen(true);
      setShownForSession(true);
    };
    t4 = [pathname, shownForSession, status, sunsetDateLabel];
    $[3] = pathname;
    $[4] = shownForSession;
    $[5] = status;
    $[6] = sunsetDateLabel;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      show: false
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isMobile || $[11] !== platform || $[12] !== sunsetDateLabel) {
    t6 = <DialogContent primitiveClose={t5} onOpenAutoFocus={_temp2} onPointerDownOutside={_temp3} onEscapeKeyDown={_temp4} className="w-[calc(100%-48px)] max-w-[345px] border-0 bg-transparent p-0 shadow-none sm:w-[536px] sm:max-w-[536px]"><div data-testid="web-sunset-modal-surface" className="flex h-[504px] w-full flex-col items-center gap-6 overflow-hidden rounded-[32px] border border-zinc-800 bg-zinc-900 pb-6 shadow-2xl sm:h-[590px] sm:rounded-[40px] sm:border-white/20 sm:bg-[rgba(39,39,42,0.4)] sm:backdrop-blur-xl">{isMobile ? <MobileContent platform={platform} sunsetDateLabel={sunsetDateLabel} /> : <DesktopContent sunsetDateLabel={sunsetDateLabel} />}</div></DialogContent>;
    $[10] = isMobile;
    $[11] = platform;
    $[12] = sunsetDateLabel;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== open || $[15] !== t6) {
    t7 = <Dialog open={open} onOpenChange={setOpen}>{t6}</Dialog>;
    $[14] = open;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp4(event_1) {
  return event_1.preventDefault();
}
function _temp3(event_0) {
  return event_0.preventDefault();
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp() {
  const qrCode = new window.Image();
  qrCode.src = WEB_SUNSET_QR_CODE_SRC;
}